import React, { useState } from 'react';
import { Button, CircularProgress, Snackbar, Alert } from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../../../app/store';
import { useUpsertContractMutation } from '../../../../../services/ContractService';
import { setIsDirty } from '../../../../../services/ContractService/contractSlice';

const SaveContractButton: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const contract = useAppSelector((state) => state.contract.currentContract);
  const isDirty = useAppSelector((state) => state.contract.isDirty);
  
  const [upsertContract, { isLoading }] = useUpsertContractMutation();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const company = searchParams.get('company') || '';

  const handleSave = async () => {
    if (!contract) {
      return;
    }

    try {
      // Call the upsert contract API
      const result = await upsertContract({
        contract: contract
      }).unwrap();

      // Mark as no longer dirty
      dispatch(setIsDirty(false));
      
      // Show success message
      setShowSuccess(true);
      
    } catch (error: any) {
      // Show error message
      const message = error?.data?.message || error?.message || 'Failed to save contract';
      setErrorMessage(message);
      setShowError(true);
      console.error('Failed to save contract:', error);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
  };

  const handleCloseError = () => {
    setShowError(false);
    setErrorMessage('');
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
        onClick={handleSave}
        disabled={!contract || isLoading || !isDirty}
        sx={{ minWidth: 120 }}
      >
        {isLoading ? 'Saving...' : 'Save Contract'}
      </Button>

      <Snackbar
        open={showSuccess}
        autoHideDuration={1500}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          Contract saved successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SaveContractButton;
